import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, FileText, AlertTriangle, ArrowLeft, Pencil, Plus, Trash2, Layers } from "lucide-react";
import { toast } from "sonner";

export default function DocumentView() {
  const { documentId } = useParams();
  const [doc, setDoc] = useState<any>(null);
  const [recs, setRecs] = useState<any[]>([]);
  const [labs, setLabs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editedTests, setEditedTests] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [editModeLabs, setEditModeLabs] = useState(false);
  const [editedLabs, setEditedLabs] = useState<any[]>([]);
  const [savingLabs, setSavingLabs] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const { data: d, error: e1 } = await supabase
          .from("documents_onboarding")
          .select("*")
          .eq("id", documentId)
          .maybeSingle();
        if (e1) throw new Error(e1.message);
        setDoc(d);

        // ✅ REMOVIDO: patient_health_records não é mais usado
        const r: any[] = [];
        setRecs(r);

        const { data: l } = await supabase
          .from("lab_results_onboarding")
          .select("*")
          .eq("source_document_id", documentId)
          .order("collected_at", { ascending: false })
          .order("created_at", { ascending: false });
        setLabs(l || []);
        setEditedLabs(l || []);

        // Inicializar dados editáveis
        if (d && d.ai_insights) {
          const structuredTests = normalizeStructuredTests(d.ai_insights);
          if (structuredTests) {
            setEditedTests(structuredTests);
          }
        }
      } catch (e: any) {
        setErr(e.message || "Falha ao carregar documento.");
      } finally {
        setLoading(false);
      }
    })();
  }, [documentId]);

  function normalizeStructuredTests(aiInsights: any): any[] | null {
    // Caso 1: formato antigo/PDF
    if (aiInsights?.structured?.tests?.length) {
      return aiInsights.structured.tests.map((t: any, idx: number) => ({
        id: `temp_${idx}`,
        name: t.name || t.test_name || t.test || "",
        result: String(t.result || t.value || ""),
        unit: t.unit || "",
        ref_low: t.ref_low || "",
        ref_high: t.ref_high || "",
        reference_range: t.reference_range || "",
        collected_at: t.date || t.collected_at || t.collection_date || "",
      }));
    }

    // Caso 2: formato novo/JPG/MIAFILE
    if (aiInsights?.analysis?.structured?.tests?.length) {
      return aiInsights.analysis.structured.tests.map((t: any, idx: number) => {
        let result = String(t.result || "");
        let unit = "";
        const unitMatch = result.match(/^([\d.,]+)\s*([a-zA-Zµ/%]+(?:\/[a-zA-Z]+)?)/);
        if (unitMatch) {
          result = unitMatch[1];
          unit = unitMatch[2];
        }

        return {
          id: `temp_${idx}`,
          name: t.test_name || t.name || "",
          result,
          unit,
          ref_low: "",
          ref_high: "",
          reference_range:
            typeof t.reference_range === "string" ? t.reference_range : JSON.stringify(t.reference_range || ""),
          collected_at: t.collection_date || t.date || "",
        };
      });
    }

    return null;
  }

  function updateTest(id: string, field: string, value: any) {
    setEditedTests((prev) => prev.map((t) => (t.id === id ? { ...t, [field]: value } : t)));
  }

  async function saveStructuredTests() {
    try {
      setSaving(true);

      // Validar campos obrigatórios
      const invalid = editedTests.find((t) => !t.name || !t.result);
      if (invalid) {
        toast.error("Todos os exames devem ter nome e resultado preenchidos");
        return;
      }

      // Atualizar doc.ai_insights no banco
      const updatedInsights = {
        ...doc.ai_insights,
        structured: {
          ...doc.ai_insights?.structured,
          tests: editedTests.map((t) => ({
            name: t.name,
            test_name: t.name,
            result: t.result,
            value: t.result,
            unit: t.unit,
            ref_low: t.ref_low ? parseFloat(t.ref_low) : null,
            ref_high: t.ref_high ? parseFloat(t.ref_high) : null,
            reference_range: t.reference_range,
            collected_at: t.collected_at,
            date: t.collected_at,
          })),
        },
      };

      const { error } = await supabase
        .from("documents_onboarding")
        .update({ ai_insights: updatedInsights })
        .eq("id", documentId);

      if (error) throw error;

      setDoc({ ...doc, ai_insights: updatedInsights });
      setEditMode(false);
      toast.success("Dados estruturados salvos com sucesso!");
    } catch (e: any) {
      toast.error(e.message || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  }

  async function generateLabResults() {
    try {
      setGenerating(true);

      // Validar campos obrigatórios
      const invalid = editedTests.find((t) => !t.name || !t.result);
      if (invalid) {
        toast.error("Todos os exames devem ter nome e resultado preenchidos");
        return;
      }

      // Mapear para formato de lab_results_onboarding
      const labResults = editedTests.map((t) => {
        const numValue = parseFloat(
          String(t.result)
            .replace(/[^0-9.,]/g, "")
            .replace(",", "."),
        );

        return {
          patient_id: doc.patient_id,
          source_document_id: doc.id,
          test_name: t.name,
          value: isNaN(numValue) ? null : numValue,
          unit: t.unit || null,
          ref_low: t.ref_low ? parseFloat(t.ref_low) : null,
          ref_high: t.ref_high ? parseFloat(t.ref_high) : null,
          collected_at: t.collected_at || null,
        };
      });

      // Inserir no banco
      const { error } = await supabase.from("lab_results_onboarding").insert(labResults);

      if (error) throw error;

      // Recarregar resultados
      const { data: newLabs } = await supabase
        .from("lab_results_onboarding")
        .select("*")
        .eq("source_document_id", documentId)
        .order("collected_at", { ascending: false });

      setLabs(newLabs || []);
      toast.success(`${labResults.length} resultado(s) gerado(s) com sucesso!`);
    } catch (e: any) {
      toast.error(e.message || "Erro ao gerar resultados");
    } finally {
      setGenerating(false);
    }
  }

  function addNewTest() {
    const newId = `temp_${Date.now()}`;
    setEditedTests((prev) => [
      ...prev,
      {
        id: newId,
        name: "",
        result: "",
        unit: "",
        ref_low: "",
        ref_high: "",
        reference_range: "",
        collected_at: "",
      },
    ]);
  }

  function removeTest(id: string) {
    setEditedTests((prev) => prev.filter((t) => t.id !== id));
  }

  // ===== Funções para edição de resultados salvos =====

  function updateLab(id: string, field: string, value: any) {
    setEditedLabs((prev) => prev.map((l) => (l.id === id ? { ...l, [field]: value } : l)));
  }

  async function saveLabResults() {
    try {
      setSavingLabs(true);

      // Validar campos obrigatórios
      const invalid = editedLabs.find(
        (l) => !l.test_name || l.value === null || l.value === undefined || l.value === "",
      );
      if (invalid) {
        toast.error("Todos os resultados devem ter nome do exame e valor preenchidos");
        return;
      }

      // Separar novos (temp_id) de existentes (uuid)
      const toUpdate = editedLabs.filter((l) => !l.id.startsWith("temp_"));
      const toInsert = editedLabs.filter((l) => l.id.startsWith("temp_"));

      // UPDATE registros existentes
      for (const lab of toUpdate) {
        const { error } = await supabase
          .from("lab_results_onboarding")
          .update({
            test_name: lab.test_name,
            value:
              parseFloat(
                String(lab.value)
                  .replace(/[^0-9.,]/g, "")
                  .replace(",", "."),
              ) || null,
            unit: lab.unit || null,
            ref_low: lab.ref_low ? parseFloat(lab.ref_low) : null,
            ref_high: lab.ref_high ? parseFloat(lab.ref_high) : null,
            collected_at: lab.collected_at || null,
          })
          .eq("id", lab.id);

        if (error) throw error;
      }

      // INSERT novos registros
      if (toInsert.length > 0) {
        const newRecords = toInsert.map((l) => ({
          patient_id: doc.patient_id,
          source_document_id: doc.id,
          test_name: l.test_name,
          value:
            parseFloat(
              String(l.value)
                .replace(/[^0-9.,]/g, "")
                .replace(",", "."),
            ) || null,
          unit: l.unit || null,
          ref_low: l.ref_low ? parseFloat(l.ref_low) : null,
          ref_high: l.ref_high ? parseFloat(l.ref_high) : null,
          collected_at: l.collected_at || null,
        }));

        const { error } = await supabase.from("lab_results_onboarding").insert(newRecords);

        if (error) throw error;
      }

      // Recarregar dados
      const { data: refreshed } = await supabase
        .from("lab_results_onboarding")
        .select("*")
        .eq("source_document_id", documentId)
        .order("collected_at", { ascending: false })
        .order("created_at", { ascending: false });

      setLabs(refreshed || []);
      setEditedLabs(refreshed || []);
      setEditModeLabs(false);
      toast.success("Resultados salvos com sucesso!");
    } catch (e: any) {
      toast.error(e.message || "Erro ao salvar resultados");
    } finally {
      setSavingLabs(false);
    }
  }

  async function removeLab(id: string) {
    if (id.startsWith("temp_")) {
      // Apenas remover do array local
      setEditedLabs((prev) => prev.filter((l) => l.id !== id));
    } else {
      // Deletar do banco
      try {
        const { error } = await supabase.from("lab_results_onboarding").delete().eq("id", id);

        if (error) throw error;
        setEditedLabs((prev) => prev.filter((l) => l.id !== id));
        toast.success("Resultado excluído");
      } catch (e: any) {
        toast.error(e.message || "Erro ao excluir");
      }
    }
  }

  function addNewLab() {
    const newId = `temp_${Date.now()}`;
    setEditedLabs((prev) => [
      ...prev,
      {
        id: newId,
        test_name: "",
        value: "",
        unit: "",
        ref_low: "",
        ref_high: "",
        collected_at: "",
      },
    ]);
  }

  if (loading)
    return (
      <div className="text-sm flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Carregando…
      </div>
    );
  if (!doc) return <div className="text-sm text-destructive">Documento não encontrado.</div>;

  return (
    <div className="space-y-6">
      <Link
        to={`/app/pacientes/${doc.patient_id}`}
        className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
      >
        <ArrowLeft className="h-4 w-4" /> Voltar ao paciente
      </Link>

      {err && <div className="text-sm text-destructive">{err}</div>}

      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Análise completa</h1>

        <button
          onClick={async () => {
            const isCSV = doc.file_type === "csv";
            const labCount = labs.length;
            const recordCount = recs.length;

            const confirmMessage = isCSV
              ? `Excluir este arquivo CSV e todos os dados associados?\n\n• ${labCount} resultado(s) de laboratório\n• ${recordCount} registro(s) de saúde\n\nEsta ação não pode ser desfeita.`
              : `Excluir este documento e todos os dados associados?\n\n• ${labCount} resultado(s) de laboratório\n• ${recordCount} registro(s) de saúde\n\nEsta ação não pode ser desfeita.`;

            if (!confirm(confirmMessage)) return;

            try {
              // ✅ REMOVIDO: patient_health_records não é mais usado
              await supabase.from("lab_results_onboarding").delete().eq("source_document_id", documentId);
              await supabase.from("documents_onboarding").delete().eq("id", documentId);

              toast.success(
                isCSV
                  ? "Arquivo CSV e dados associados excluídos com sucesso"
                  : "Documento e dados associados excluídos com sucesso",
              );

              window.location.href = `/app/pacientes/${doc.patient_id}`;
            } catch (e: any) {
              toast.error(e.message || "Erro ao excluir documento");
            }
          }}
          className="px-4 py-2 rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90 inline-flex items-center gap-2 text-sm font-medium transition-colors"
        >
          <Trash2 className="h-4 w-4" /> Excluir Arquivo
        </button>
      </div>

      <section className="rounded-xl border bg-card p-4 shadow-card">
        <div className="flex items-start gap-3">
          <FileText className="h-5 w-5 mt-0.5" />
          <div>
            <div className="font-semibold">{doc.file_name}</div>
            <div className="text-xs text-muted-foreground">
              Tipo: {doc.file_type?.toUpperCase()} • Tamanho: {doc.file_size} bytes • Enviado em{" "}
              {new Date(doc.created_at).toLocaleString()}
            </div>
            {doc.status === "error" && (
              <div className="mt-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 inline-flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" /> {doc.processing_status || "Erro ao processar"}
              </div>
            )}
          </div>
        </div>

        {doc.ai_summary && (
          <div className="mt-4">
            <div className="text-sm font-medium">Análise IA (resumo do documento)</div>
            <p className="text-sm">{doc.ai_summary}</p>
          </div>
        )}

        {doc.extracted_text && (
          <details className="mt-4">
            <summary className="cursor-pointer text-sm text-primary">Ver texto extraído</summary>
            <pre className="mt-2 text-xs whitespace-pre-wrap bg-muted rounded-lg p-3 border">{doc.extracted_text}</pre>
          </details>
        )}

        {editedTests.length > 0 && (
          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Dados estruturados ({editedTests.length} exame(s))</div>
              <div className="flex gap-2">
                {!editMode ? (
                  <>
                    <button
                      onClick={() => setEditMode(true)}
                      className="px-3 py-1.5 rounded-lg border border-primary text-primary hover:bg-primary/10 inline-flex items-center gap-1 text-sm transition-colors"
                    >
                      <Pencil className="h-4 w-4" /> Editar
                    </button>
                    <button
                      onClick={generateLabResults}
                      disabled={generating || labs.length > 0}
                      className="px-3 py-1.5 rounded-lg bg-accent text-accent-foreground hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1 text-sm transition-colors"
                    >
                      {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Layers className="h-4 w-4" />}
                      {generating ? "Gerando..." : "Gerar Resultados"}
                    </button>
                    {doc.file_type !== "csv" && (
                      <button
                        onClick={async () => {
                          if (
                            !confirm(
                              "Excluir tudo associado a este documento (registros e resultados)? Esta ação não pode ser desfeita.",
                            )
                          )
                            return;
                          try {
                            // ✅ REMOVIDO: patient_health_records não é mais usado
                            await supabase.from("lab_results_onboarding").delete().eq("source_document_id", documentId);
                            await supabase.from("documents_onboarding").delete().eq("id", documentId);
                            toast.success("Documento e dados associados excluídos");
                            window.history.back();
                          } catch (e: any) {
                            toast.error(e.message || "Erro ao excluir");
                          }
                        }}
                        className="px-3 py-1.5 rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90 inline-flex items-center gap-1 text-sm transition-colors"
                      >
                        <Trash2 className="h-4 w-4" /> Excluir tudo
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <button
                      onClick={addNewTest}
                      className="px-3 py-1.5 rounded-lg border border-primary text-primary hover:bg-primary/10 inline-flex items-center gap-1 text-sm transition-colors"
                    >
                      <Plus className="h-4 w-4" /> Adicionar
                    </button>
                    <button
                      onClick={() => {
                        setEditedTests(normalizeStructuredTests(doc.ai_insights) || []);
                        setEditMode(false);
                      }}
                      className="px-3 py-1.5 rounded-lg border border-border hover:bg-muted inline-flex items-center gap-1 text-sm transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={saveStructuredTests}
                      disabled={saving}
                      className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-1 text-sm transition-colors"
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      {saving ? "Salvando..." : "Salvar"}
                    </button>
                  </>
                )}
              </div>
            </div>

            {labs.length > 0 && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 inline-flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Resultados já gerados. Delete-os na seção abaixo para gerar novamente.
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border">
                <thead>
                  <tr className="bg-muted">
                    <th className="py-2 px-3 text-left font-medium">Exame</th>
                    <th className="py-2 px-3 text-left font-medium">Resultado</th>
                    <th className="py-2 px-3 text-left font-medium">Unidade</th>
                    <th className="py-2 px-3 text-left font-medium">Ref. Baixa</th>
                    <th className="py-2 px-3 text-left font-medium">Ref. Alta</th>
                    <th className="py-2 px-3 text-left font-medium">Data Coleta</th>
                    {editMode && <th className="py-2 px-3 text-left font-medium">Ações</th>}
                  </tr>
                </thead>
                <tbody>
                  {editedTests.map((t) => (
                    <tr key={t.id} className="border-b">
                      <td className="py-2 px-3">
                        {editMode ? (
                          <input
                            type="text"
                            value={t.name}
                            onChange={(e) => updateTest(t.id, "name", e.target.value)}
                            className="w-full px-2 py-1 border rounded text-sm"
                            placeholder="Nome do exame"
                          />
                        ) : (
                          t.name || "—"
                        )}
                      </td>
                      <td className="py-2 px-3">
                        {editMode ? (
                          <input
                            type="text"
                            value={t.result}
                            onChange={(e) => updateTest(t.id, "result", e.target.value)}
                            className="w-full px-2 py-1 border rounded text-sm"
                            placeholder="Resultado"
                          />
                        ) : (
                          t.result || "—"
                        )}
                      </td>
                      <td className="py-2 px-3">
                        {editMode ? (
                          <input
                            type="text"
                            value={t.unit}
                            onChange={(e) => updateTest(t.id, "unit", e.target.value)}
                            className="w-full px-2 py-1 border rounded text-sm"
                            placeholder="mg/dL"
                          />
                        ) : (
                          t.unit || "—"
                        )}
                      </td>
                      <td className="py-2 px-3">
                        {editMode ? (
                          <input
                            type="text"
                            value={t.ref_low}
                            onChange={(e) => updateTest(t.id, "ref_low", e.target.value)}
                            className="w-full px-2 py-1 border rounded text-sm"
                            placeholder="0"
                          />
                        ) : (
                          t.ref_low || "—"
                        )}
                      </td>
                      <td className="py-2 px-3">
                        {editMode ? (
                          <input
                            type="text"
                            value={t.ref_high}
                            onChange={(e) => updateTest(t.id, "ref_high", e.target.value)}
                            className="w-full px-2 py-1 border rounded text-sm"
                            placeholder="100"
                          />
                        ) : (
                          t.ref_high || "—"
                        )}
                      </td>
                      <td className="py-2 px-3">
                        {editMode ? (
                          <input
                            type="date"
                            value={t.collected_at ? t.collected_at.slice(0, 10) : ""}
                            onChange={(e) => updateTest(t.id, "collected_at", e.target.value)}
                            className="w-full px-2 py-1 border rounded text-sm"
                          />
                        ) : (
                          (() => {
                            if (!t.collected_at) return "—";
                            const date = new Date(t.collected_at);
                            return isNaN(date.getTime()) ? "—" : date.toLocaleDateString("pt-BR");
                          })()
                        )}
                      </td>
                      {editMode && (
                        <td className="py-2 px-3">
                          <button
                            onClick={() => removeTest(t.id)}
                            className="p-1 text-destructive hover:bg-destructive/10 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      <section className="rounded-xl border bg-card p-4 shadow-card">
        <div className="font-semibold mb-3">Registros vinculados</div>
        {!recs.length ? (
          <div className="text-sm text-muted-foreground">Nenhum.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2 pr-4">Data</th>
                  <th className="py-2 pr-4">Tipo</th>
                  <th className="py-2 pr-4">Texto</th>
                </tr>
              </thead>
              <tbody>
                {recs.map((r) => (
                  <tr key={r.id} className="border-b">
                    <td className="py-2 pr-4">{r.observed_at ? new Date(r.observed_at).toLocaleDateString() : "—"}</td>
                    <td className="py-2 pr-4">{r.type}</td>
                    <td className="py-2 pr-4 max-w-[520px]">
                      <div className="line-clamp-3">{r.text || r.content || "—"}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="rounded-xl border bg-card p-4 shadow-card">
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold">Resultados estruturados salvos ({editedLabs.length})</div>
          <div className="flex gap-2">
            {!editModeLabs ? (
              <>
                {labs.length > 0 && (
                  <>
                    <button
                      onClick={() => setEditModeLabs(true)}
                      className="px-3 py-1.5 rounded-lg border border-primary text-primary hover:bg-primary/10 inline-flex items-center gap-1 text-sm transition-colors"
                    >
                      <Pencil className="h-4 w-4" /> Editar
                    </button>
                    <button
                      onClick={async () => {
                        if (!confirm("Excluir todos os resultados gerados a partir deste documento?")) return;
                        try {
                          const { error } = await supabase
                            .from("lab_results_onboarding")
                            .delete()
                            .eq("source_document_id", documentId);

                          if (error) throw error;
                          setLabs([]);
                          setEditedLabs([]);
                          toast.success("Resultados excluídos");
                        } catch (e: any) {
                          toast.error(e.message || "Erro ao excluir");
                        }
                      }}
                      className="px-3 py-1.5 rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90 inline-flex items-center gap-1 text-sm transition-colors"
                    >
                      <Trash2 className="h-4 w-4" /> Excluir todos
                    </button>
                  </>
                )}
              </>
            ) : (
              <>
                <button
                  onClick={addNewLab}
                  className="px-3 py-1.5 rounded-lg border border-primary text-primary hover:bg-primary/10 inline-flex items-center gap-1 text-sm transition-colors"
                >
                  <Plus className="h-4 w-4" /> Adicionar
                </button>
                <button
                  onClick={() => {
                    setEditedLabs([...labs]);
                    setEditModeLabs(false);
                  }}
                  className="px-3 py-1.5 rounded-lg border border-border hover:bg-muted inline-flex items-center gap-1 text-sm transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={saveLabResults}
                  disabled={savingLabs}
                  className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-1 text-sm transition-colors"
                >
                  {savingLabs ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {savingLabs ? "Salvando..." : "Salvar"}
                </button>
              </>
            )}
          </div>
        </div>

        {!editedLabs.length ? (
          <div className="text-sm text-muted-foreground">
            Nenhum resultado gerado ainda. Use o botão "Gerar Resultados" acima para criar.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm border">
              <thead>
                <tr className="bg-muted">
                  <th className="py-2 px-3 text-left font-medium">Data Coleta</th>
                  <th className="py-2 px-3 text-left font-medium">Nome do Exame</th>
                  <th className="py-2 px-3 text-left font-medium">Valor</th>
                  <th className="py-2 px-3 text-left font-medium">Unidade</th>
                  <th className="py-2 px-3 text-left font-medium">Ref. Baixa</th>
                  <th className="py-2 px-3 text-left font-medium">Ref. Alta</th>
                  {editModeLabs && <th className="py-2 px-3 text-left font-medium">Ações</th>}
                </tr>
              </thead>
              <tbody>
                {editedLabs.map((l) => (
                  <tr key={l.id} className="border-b">
                    <td className="py-2 px-3">
                      {editModeLabs ? (
                        <input
                          type="date"
                          value={l.collected_at ? String(l.collected_at).slice(0, 10) : ""}
                          onChange={(e) => updateLab(l.id, "collected_at", e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm"
                        />
                      ) : l.collected_at ? (
                        new Date(l.collected_at).toLocaleDateString("pt-BR")
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="py-2 px-3">
                      {editModeLabs ? (
                        <input
                          type="text"
                          value={l.test_name}
                          onChange={(e) => updateLab(l.id, "test_name", e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm"
                          placeholder="Nome do exame"
                        />
                      ) : (
                        l.test_name || "—"
                      )}
                    </td>
                    <td className="py-2 px-3">
                      {editModeLabs ? (
                        <input
                          type="text"
                          value={l.value ?? ""}
                          onChange={(e) => updateLab(l.id, "value", e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm"
                          placeholder="Valor"
                        />
                      ) : (
                        (l.value ?? "—")
                      )}
                    </td>
                    <td className="py-2 px-3">
                      {editModeLabs ? (
                        <input
                          type="text"
                          value={l.unit || ""}
                          onChange={(e) => updateLab(l.id, "unit", e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm"
                          placeholder="mg/dL"
                        />
                      ) : (
                        l.unit || "—"
                      )}
                    </td>
                    <td className="py-2 px-3">
                      {editModeLabs ? (
                        <input
                          type="text"
                          value={l.ref_low ?? ""}
                          onChange={(e) => updateLab(l.id, "ref_low", e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm"
                          placeholder="0"
                        />
                      ) : (
                        (l.ref_low ?? "—")
                      )}
                    </td>
                    <td className="py-2 px-3">
                      {editModeLabs ? (
                        <input
                          type="text"
                          value={l.ref_high ?? ""}
                          onChange={(e) => updateLab(l.id, "ref_high", e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm"
                          placeholder="100"
                        />
                      ) : (
                        (l.ref_high ?? "—")
                      )}
                    </td>
                    {editModeLabs && (
                      <td className="py-2 px-3">
                        <button
                          onClick={() => removeLab(l.id)}
                          className="p-1 text-destructive hover:bg-destructive/10 rounded"
                          title="Excluir resultado"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
